import { FC, useState, useEffect, Suspense, useRef } from 'react'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import '@/components/tokens/variables.css'
import UserProfile from '../../features/auth/components/UserProfile'
import MobileNav from './MobileNav'
import MossBrand from '../../features/moss-chat/components/MossBrand';
import BrandLoading from '@/components/common/BrandLoading'
import { useResponsive } from '@/hooks/useResponsive'
import { useAuthStore } from '@/stores/authStore'
import { ICONS } from '@/constants/icons'
import { useVersionCheck } from '@/hooks/useVersionCheck'
import { UpdateNotification } from '@/components/UpdateNotification'

// 页面切换加载组件 - 使用统一品牌 Loading
const PageLoading = () => {
  return (
    <div className="w-full h-full flex items-center justify-center">
      <BrandLoading size="md" tip="加载中..." />
    </div>
  );
};

const Layout: FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isMobile, isTablet } = useResponsive()
  const isMossUser = useAuthStore((state) => state.isMossUser)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => window.innerWidth < 1180)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const [isNavTransitioning, setIsNavTransitioning] = useState(false)
  const prevCollapsed = useRef(isSidebarCollapsed)

  // 🆕 版本更新检测
  const { hasUpdate, newVersion, refresh, dismiss } = useVersionCheck({
    checkOnRouteChange: true,
    checkOnVisibilityChange: true,
    minCheckInterval: 60000,
  })

  // 判断当前用户是否需要隐藏工作流和运行日志
  const shouldHideWorkflowAndRunlog = isMossUser()

  const isActive = (path: string) => location.pathname.startsWith(path)

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed)
  }

  // 监听折叠状态变化，触发过渡动画
  useEffect(() => {
    if (prevCollapsed.current !== isSidebarCollapsed) {
      setIsNavTransitioning(true)
      const timer = setTimeout(() => setIsNavTransitioning(false), 300)
      prevCollapsed.current = isSidebarCollapsed
      return () => clearTimeout(timer)
    }
  }, [isSidebarCollapsed])

  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.altKey && event.key === 's') {
      toggleSidebar();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 1180) {
        setIsSidebarCollapsed(true);
      } else {
        setIsSidebarCollapsed(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      className="flex h-screen bg-page-base overflow-hidden relative font-sans text-content-primary selection:bg-fashion-green selection:text-black"
      data-nav-collapsed={isSidebarCollapsed}
    >
      {/* 🆕 版本更新提示 */}
      <UpdateNotification
        visible={hasUpdate}
        newVersion={newVersion}
        onRefresh={refresh}
        onDismiss={dismiss}
      />

      {/* Mobile Nav Drawer */}
      <MobileNav isOpen={isMobileNavOpen} onClose={() => setIsMobileNavOpen(false)} />

      {/* 导航栏折叠过渡遮罩 - 淡化空白区域 */}
      <AnimatePresence>
        {isNavTransitioning && (
          <motion.div
            className="fixed top-0 bg-page-base z-[25] hidden lg:block pointer-events-none"
            style={{
              left: isSidebarCollapsed ? 80 : 80,
              width: 140,
              height: '100vh'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
          />
        )}
      </AnimatePresence>

      {/* Sidebar - High Fashion Tech */}
      <aside
        className={`${isSidebarCollapsed ? 'w-[80px]' : 'w-[220px]'} flex-col transition-all duration-300 ease-out relative z-20 hidden lg:flex bg-page-side border-r border-line-subtle`}
      >
        {/* Logo Area */}
        <div className={`h-[64px] flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'} px-4`}>
          {!isSidebarCollapsed && (
            <div
              className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => navigate('/welcome')}
            >
              <MossBrand size="tiny" />
              <span className="text-white font-bold text-lg tracking-wide">Vinka</span>
            </div>
          )}
          <button
            onClick={toggleSidebar}
            className={`flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/5 transition-all duration-200 ${isSidebarCollapsed ? 'w-auto h-auto py-2' : 'w-7 h-7'}`}
            title={isSidebarCollapsed ? "展开" : "收起"}
          >
            {isSidebarCollapsed ? (
              <ICONS.UI.MENU_EXPAND className="text-lg" />
            ) : (
              <ICONS.UI.SIDEBAR_COLLAPSE className="text-lg" />
            )}
          </button>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto pt-0 pb-8 px-4 space-y-0.5">
          {/* 工作流 - 当用户名包含 vinka 时隐藏 */}
          {!shouldHideWorkflowAndRunlog && (
            <Link
              to="/app/workflow"
              className={`group flex items-center ${isSidebarCollapsed ? 'justify-center px-0' : 'px-0'} py-2 rounded-xl transition-all duration-300 ${isActive('/app/workflow')
                ? 'bg-white/5 text-content-primary shadow-[0_0_20px_rgba(0,255,148,0.1)] border border-line-subtle'
                : 'text-content-muted hover:text-content-primary hover:bg-white/5 hover:border hover:border-line-subtle border border-transparent'
                }`}
            >
              <ICONS.NAV.WORKFLOW className={`text-lg transition-colors ${isActive('/app/workflow') ? 'text-fashion-green' : 'text-content-secondary group-hover:text-content-primary'}`} />
              {!isSidebarCollapsed && (
                <span className="ml-4 text-[15px] font-bold tracking-wide">工作流</span>
              )}
              {!isSidebarCollapsed && isActive('/app/workflow') && (
                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-fashion-green shadow-[0_0_10px_#00FF94]"></div>
              )}
            </Link>
          )}

          <Link
            to="/app/moss-chat"
            className={`group flex items-center ${isSidebarCollapsed ? 'justify-center px-0' : 'px-0'} py-2 rounded-xl transition-all duration-300 ${isActive('/app/moss-chat')
              ? 'bg-white/5 text-content-primary shadow-[0_0_20px_rgba(19,194,194,0.1)] border border-line-subtle'
              : 'text-content-muted hover:text-content-primary hover:bg-white/5 hover:border hover:border-line-subtle border border-transparent'
              }`}
          >
            <ICONS.NAV.AGENT className={`text-lg transition-colors ${isActive('/app/moss-chat') ? 'text-fashion-pink' : 'text-content-secondary group-hover:text-content-primary'}`} />
            {!isSidebarCollapsed && (
              <span className="ml-4 text-[15px] font-bold tracking-wide">智能体</span>
            )}
            {!isSidebarCollapsed && isActive('/app/moss-chat') && (
              <div className="ml-auto w-1.5 h-1.5 rounded-full bg-fashion-pink shadow-[0_0_10px_#13c2c2]"></div>
            )}
          </Link>

          <Link
            to="/app/skills"
            className={`group flex items-center ${isSidebarCollapsed ? 'justify-center px-0' : 'px-0'} py-2 rounded-xl transition-all duration-300 ${isActive('/app/skills')
              ? 'bg-white/5 text-content-primary shadow-[0_0_20px_rgba(19,194,194,0.1)] border border-line-subtle'
              : 'text-content-muted hover:text-content-primary hover:bg-white/5 hover:border hover:border-line-subtle border border-transparent'
              }`}
          >
            <ICONS.FILE.FOLDER className={`text-lg transition-colors ${isActive('/app/skills') ? 'text-fashion-pink' : 'text-content-secondary group-hover:text-content-primary'}`} />
            {!isSidebarCollapsed && (
              <span className="ml-4 text-[15px] font-bold tracking-wide">我的技能</span>
            )}
            {!isSidebarCollapsed && isActive('/app/skills') && (
              <div className="ml-auto w-1.5 h-1.5 rounded-full bg-fashion-pink shadow-[0_0_10px_#13c2c2]"></div>
            )}
          </Link>

          {/* 运行日志 - 当用户名包含 vinka 时隐藏 */}
          {!shouldHideWorkflowAndRunlog && (
            <Link
              to="/app/runlog"
              className={`group flex items-center ${isSidebarCollapsed ? 'justify-center px-0' : 'px-0'} py-2 rounded-xl transition-all duration-300 ${isActive('/app/runlog')
                ? 'bg-white/5 text-content-primary shadow-[0_0_20px_rgba(0,240,255,0.1)] border border-line-subtle'
                : 'text-content-muted hover:text-content-primary hover:bg-white/5 hover:border hover:border-line-subtle border border-transparent'
                }`}
            >
              <ICONS.NAV.RUNLOG className={`text-lg transition-colors ${isActive('/app/runlog') ? 'text-fashion-cyan' : 'text-content-secondary group-hover:text-content-primary'}`} />
              {!isSidebarCollapsed && (
                <span className="ml-4 text-[15px] font-bold tracking-wide">运行日志</span>
              )}
              {!isSidebarCollapsed && isActive('/app/runlog') && (
                <div className="ml-auto w-1.5 h-1.5 rounded-full bg-fashion-cyan shadow-[0_0_10px_#00F0FF]"></div>
              )}
            </Link>
          )}

          <Link
            to="/app/image-chat"
            className={`group flex items-center ${isSidebarCollapsed ? 'justify-center px-0' : 'px-0'} py-2 rounded-xl transition-all duration-300 ${isActive('/app/image-chat')
              ? 'bg-white/10 text-white shadow-[0_0_20px_rgba(255,107,107,0.1)] border border-white/10'
              : 'text-white/40 hover:text-white hover:bg-white/5 hover:border hover:border-white/5 border border-transparent'
              }`}
          >
            <ICONS.NAV.CANVAS className={`text-lg transition-colors ${isActive('/app/image-chat') ? 'text-fashion-pink' : 'text-content-secondary group-hover:text-content-primary'}`} />
            {!isSidebarCollapsed && (
              <span className="ml-4 text-[15px] font-bold tracking-wide">创意画布</span>
            )}
            {!isSidebarCollapsed && isActive('/app/image-chat') && (
              <div className="ml-auto w-1.5 h-1.5 rounded-full bg-fashion-pink shadow-[0_0_10px_#ff6b6b]"></div>
            )}
          </Link>
        </nav>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden bg-page-base relative z-10">
        {/* Top Bar */}
        <div className="h-[80px] flex items-center justify-between px-8 bg-page-base/80 backdrop-blur-xl border-b border-line-subtle z-20 sticky top-0">
          <div className="flex items-center gap-4">
            {(isMobile || isTablet) && (
              <button
                onClick={() => setIsMobileNavOpen(true)}
                className="p-2 rounded-lg hover:bg-white/5 transition-colors lg:hidden text-content-primary"
              >
                <ICONS.UI.MENU_EXPAND className="text-xl" />
              </button>
            )}

            <div className="hidden md:block">
              <h2 className="text-xl font-black text-content-primary tracking-tight flex items-center gap-2">
                <span className="text-content-muted">/</span>
                {location.pathname === '/app/workflow' ? '工作流中心' :
                  location.pathname.startsWith('/app/skills') ? '我的技能' :
                  location.pathname.startsWith('/app/moss-chat') ? '企业智能体' :
                    location.pathname === '/app/runlog' ? '系统日志' :
                      location.pathname === '/app/image-chat' ? '创意画布' : 
                      location.pathname === '/app/image-chat/gallery' ? '发现创意' : '工作流中心'}
              </h2>
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            <UserProfile />
          </div>
        </div>

        {/* Page Content */}
        <div className="flex-1 overflow-hidden bg-page-base relative">
          <div className="relative z-10 h-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
                className="h-full"
              >
                <Suspense fallback={<PageLoading />}>
                  <Outlet />
                </Suspense>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  )
}

export default Layout
